import { MoreHorizontal, Plus, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  deleteNotice,
  listNotices,
  NOTICE_CATEGORY_OPTIONS,
  type NoticeDefinition,
  NOTICE_SEVERITY_OPTIONS,
  updateNotice,
} from "@/features/reference/api";
import { ConfirmDialog } from "./confirm-dialog";
import { NoticeDialog } from "./notice-dialog";
import { ReferencePanel } from "./reference-panel";

const CATEGORY_LABELS = Object.fromEntries(NOTICE_CATEGORY_OPTIONS.map((o) => [o.value, o.label]));
const SEVERITY_LABELS = Object.fromEntries(NOTICE_SEVERITY_OPTIONS.map((o) => [o.value, o.label]));
const SEVERITY_VARIANT: Record<string, "secondary" | "outline" | "destructive"> = {
  INFO: "secondary",
  ACTION_REQUIRED: "outline",
  URGENT: "destructive",
};

export function NoticesPage() {
  const [items, setItems] = useState<NoticeDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [active, setActive] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<NoticeDefinition | undefined>();
  const [removing, setRemoving] = useState<NoticeDefinition | undefined>();

  const refetch = useCallback(() => {
    setLoading(true);
    listNotices({
      search: search || undefined,
      category: category === "all" ? undefined : category,
      severity: severity === "all" ? undefined : severity,
      active: active === "all" ? undefined : active,
      pageSize: 100,
    })
      .then((r) => {
        setItems(r.items);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [search, category, severity, active]);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <ReferencePanel
      title="Notice Dictionary"
      description="What each inbound IRS notice code means and the recommended next step."
      loading={loading && items.length === 0 && !error}
      error={error}
      action={
        <Button
          size="sm"
          className="gap-1.5"
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
        >
          <Plus className="size-4" /> Add notice
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            placeholder="Search code, title, description..."
            className="pl-9"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <FilterSelect value={category} onChange={setCategory} allLabel="All categories" options={NOTICE_CATEGORY_OPTIONS} width="w-[190px]" />
        <FilterSelect value={severity} onChange={setSeverity} allLabel="Any severity" options={NOTICE_SEVERITY_OPTIONS} width="w-[150px]" />
        <FilterSelect
          value={active}
          onChange={setActive}
          allLabel="Any status"
          options={[
            { value: "true", label: "Active" },
            { value: "false", label: "Retired" },
          ]}
          width="w-[130px]"
        />
      </div>

      {items.length === 0 ? (
        <p className="text-muted-foreground text-sm">No notices match these filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((n) => (
                <TableRow key={n.id}>
                  <TableCell className="font-mono text-xs font-medium">{n.code}</TableCell>
                  <TableCell className="font-medium">{n.title}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {CATEGORY_LABELS[n.category]}
                  </TableCell>
                  <TableCell>
                    <Badge variant={SEVERITY_VARIANT[n.severity]}>{SEVERITY_LABELS[n.severity]}</Badge>
                  </TableCell>
                  <TableCell>
                    {n.isActive ? (
                      <Badge variant="secondary">Active</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">Retired</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" aria-label="Notice actions">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => {
                              setEditing(n);
                              setDialogOpen(true);
                            }}
                          >
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => updateNotice(n.id, { ...n, isActive: !n.isActive }).then(refetch)}
                          >
                            {n.isActive ? "Retire" : "Reactivate"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(n)}>
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NoticeDialog
        key={editing?.id ?? "new"}
        notice={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={refetch}
      />
      {removing && (
        <ConfirmDialog
          title={`Delete notice ${removing.code}?`}
          description="If it's referenced by any tracked notice this will fail — retire it instead."
          confirmLabel="Delete"
          destructive
          onConfirm={() => deleteNotice(removing.id)}
          onClose={() => setRemoving(undefined)}
          onDone={refetch}
          successMessage="Notice deleted"
          fallbackError="Couldn't delete the notice."
        />
      )}
    </ReferencePanel>
  );
}

function FilterSelect({
  value,
  onChange,
  allLabel,
  options,
  width,
}: {
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  options: { value: string; label: string }[];
  width: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" className={width}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
